import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import client from "../lib/api";
import { money } from "../lib/helpers";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, CircleNotch, Heart, Lock, Package } from "@phosphor-icons/react";
import { BrandMark } from "../components/BrandMark";

export default function PaymentResult({ cancel }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState(cancel ? "cancelled" : "checking");
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (cancel) return;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const r = await client.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { setDetails(r.data); setState("paid"); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (tries < 12) setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const M = {
    checking: { icon: <CircleNotch size={48} weight="bold" className="animate-spin text-[#4A0000]" />, title: "Confirming your payment…", sub: "Hang tight, this only takes a moment." },
    paid: { icon: <CheckCircle size={52} weight="fill" className="text-green-600" />, title: "Payment received", sub: "Thank you — your photographer has been notified." },
    cancelled: { icon: <XCircle size={48} weight="fill" className="text-[#5C5A58]" />, title: "Payment cancelled", sub: "No charge was made. You can pick up right where you left off." },
    error: { icon: <XCircle size={48} weight="fill" className="text-red-600" />, title: "Payment issue", sub: "We couldn't confirm the payment. Please try again." },
    timeout: { icon: <CircleNotch size={48} weight="bold" className="text-[#B08D57]" />, title: "Still processing", sub: "Your payment is taking longer than usual. Check the Payments tab shortly." },
  }[state];

  const paid = state === "paid";
  const base = details ? Math.max(0, (details.amount || 0) - (details.tip || 0)) : 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBFAF7] p-6" data-testid="payment-result">
      <div className="max-w-md w-full text-center lux-card rounded-[5px] p-10 fade-up">
        <div className="flex justify-center mb-6"><BrandMark logoSize={34} /></div>
        <div className={`flex justify-center mb-4 ${paid ? "fade-up" : ""}`}>{M.icon}</div>
        <h1 className="font-display text-3xl mb-2" data-testid={`payment-${state}`}>{M.title}</h1>
        <p className="text-[#5C5A58] mb-6">{M.sub}</p>

        {paid && details && (
          <div className="text-left border border-[#E9DDC6] rounded-md bg-[#FBFAF7] p-5 mb-6" data-testid="payment-receipt">
            {details.title && (
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-[#5C5A58]">Shoot</span>
                <span className="font-medium truncate ml-3">{details.title}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#5C5A58]">Package{details.discount_code ? ` (code ${details.discount_code})` : ""}</span>
              <span className="font-mono-num">{money(base)}</span>
            </div>
            {details.tip > 0 && (
              <div className="flex items-center justify-between text-sm mt-1 text-[#2e7d32]">
                <span className="flex items-center gap-1"><Heart size={13} weight="fill" />Tip to photographer</span>
                <span className="font-mono-num">{money(details.tip)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-[#E9DDC6] mt-3 pt-3">
              <span className="text-sm font-medium">Total paid</span>
              <span className="font-mono-num text-lg font-semibold text-[#4A0000]" data-testid="payment-total">{money(details.amount)}</span>
            </div>
          </div>
        )}

        {paid && (
          <div className="flex items-center justify-center gap-2 text-sm text-[#4A0000] bg-[#B08D57]/10 border border-[#B08D57]/30 rounded-md py-2.5 px-3 mb-6" data-testid="deliverables-unlocked">
            <Package size={18} weight="duotone" />Your deliverables are now unlocked
          </div>
        )}

        <Button data-testid="back-to-app" onClick={() => nav("/")} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white w-full">
          {paid ? "View my deliverables" : "Back to dashboard"}
        </Button>

        <p className="flex items-center justify-center gap-1.5 text-xs text-[#9A968E] mt-5">
          <Lock size={13} weight="duotone" />Payments are securely processed by Stripe
        </p>
      </div>
    </div>
  );
}
